import { cn } from "@/lib/utils";

interface HoloProgressBarProps {
  value: number; // 0-100
  className?: string;
  size?: "sm" | "md" | "lg";
  variant?: "primary" | "secondary" | "accent" | "emerald";
  showSegments?: boolean;
  segmentCount?: number;
}

const sizeStyles = {
  sm: "h-2",
  md: "h-3",
  lg: "h-4",
};

const variantGradients: Record<string, string> = {
  primary: "from-primary via-glow-purple to-primary",
  secondary: "from-secondary via-primary to-secondary",
  accent: "from-accent via-primary to-accent",
  emerald: "from-emerald-400 via-cyan-soft to-emerald-400",
};

const variantGlows: Record<string, string> = {
  primary: "shadow-[0_0_8px_hsl(225_70%_60%/0.5),0_0_16px_hsl(260_50%_65%/0.3)]",
  secondary: "shadow-[0_0_8px_hsl(260_50%_65%/0.5),0_0_16px_hsl(225_70%_60%/0.3)]",
  accent: "shadow-[0_0_8px_hsl(195_70%_65%/0.5),0_0_16px_hsl(225_70%_60%/0.3)]",
  emerald: "shadow-[0_0_8px_hsl(160_60%_45%/0.5),0_0_16px_hsl(195_70%_65%/0.3)]",
};

const HoloProgressBar = ({
  value,
  className,
  size = "md",
  variant = "primary",
  showSegments = false,
  segmentCount = 10,
}: HoloProgressBarProps) => {
  const clampedValue = Math.max(0, Math.min(100, value));

  return (
    <div className={cn("relative rounded-full overflow-hidden", sizeStyles[size], className)}>
      {/* Track */}
      <div className="absolute inset-0 rounded-full bg-muted/60 backdrop-blur-sm border border-border/30" />
      
      {/* Fill */}
      <div
        className={cn(
          "absolute inset-y-0 left-0 rounded-full bg-gradient-to-r transition-all duration-700 ease-out",
          variantGradients[variant],
          clampedValue > 0 && variantGlows[variant]
        )}
        style={{ width: `${clampedValue}%` }}
      >
        {/* Shimmer overlay */}
        <div className="absolute inset-0 rounded-full overflow-hidden">
          <div className="holo-shimmer absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent" />
        </div>
      </div>

      {/* Segments */}
      {showSegments && (
        <div className="absolute inset-0 flex">
          {Array.from({ length: segmentCount - 1 }).map((_, i) => (
            <div
              key={i}
              className="flex-1 border-r border-background/20 last:border-r-0"
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default HoloProgressBar;
